import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";

// Hilfsfunktion: Zeit-String in HH:mm normalisieren (auch für Timestamps, Sekunden, Leerzeichen)
function normalizeTime(t) {
    if (!t) return "";
    if (t.includes("T")) t = t.split("T")[1];
    t = t.trim().split(".")[0];
    const [h, m] = t.split(":");
    return `${(h || "00").padStart(2, "0")}:${(m || "00").padStart(2, "0")}`;
}

// Generiert Zeitslots von 09:00 bis 19:30 im 30-Minuten-Takt
function generateTimeSlots() {
    const slots = [];
    let hour = 9;
    let minute = 0;
    while (hour < 19 || (hour === 19 && minute <= 30)) {
        const formatted = `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`;
        slots.push(formatted);
        minute += 30;
        if (minute >= 60) {
            hour += 1;
            minute -= 60;
        }
    }
    return slots;
}

// Hilfsfunktion: Gesamtdauer aller gewählten Dienstleistungen
function getTotalDuration(servicesArr) {
    if (!servicesArr || servicesArr.length === 0) return 0;
    let total = 0;
    servicesArr.forEach(service => {
        if (service === "Haare und Bart") total += 45;
        else if (service === "Bart" || service === "Augenbrauen zupfen") total += 15;
        else total += 30; // Haare
    });
    return total;
}

// Hilfsfunktionen für lokales Datum ohne Zeitzonenverschiebung
function formatLocalYYYYMMDD(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
function parseLocalYYYYMMDD(value) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
}

function AdminCreateAppointment() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedDate, setSelectedDate] = useState(formatLocalYYYYMMDD(new Date()));
    const [availableTimes, setAvailableTimes] = useState({ free: [], all: [], booked: [] });
    const [bookedSlots, setBookedSlots] = useState(new Set()); // Zusätzlicher State für belegte Slots
    const [isLoadingTimes, setIsLoadingTimes] = useState(false);
    const [selectedTime, setSelectedTime] = useState("");
    const [services, setServices] = useState([]); // Mehrfachauswahl
    const [serviceModalOpen, setServiceModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [search, setSearch] = useState("");
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            const { data, error } = await supabase.from("users").select("id, username");
            if (error) {
                setError("Fehler beim Laden der Nutzer.");
            } else {
                setUsers(data || []);
            }
            setLoading(false);
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        if (selectedUser && selectedDate) {
            // WICHTIG: Reset selectedTime wenn sich Datum oder User ändert
            setSelectedTime("");
            fetchAvailableTimes();
        }
        // eslint-disable-next-line
    }, [selectedUser, selectedDate]);

    // Zusätzlicher Effect für Services - reset selectedTime wenn sich Services ändern
    useEffect(() => {
        if (selectedTime && services.length > 0) {
            // Prüfe ob die gewählte Zeit noch verfügbar ist mit den neuen Services
            const totalDuration = getTotalDuration(services);
            const slotIndex = availableTimes.all.indexOf(selectedTime);
            const slotsNeeded = Math.ceil(totalDuration / 30);
            
            if (slotIndex !== -1) {
                const requiredSlots = availableTimes.all.slice(slotIndex, slotIndex + slotsNeeded);
                const canStillBook = requiredSlots.length === slotsNeeded && 
                                   requiredSlots.every(time => !bookedSlots.has(time) && availableTimes.free.includes(time));
                
                if (!canStillBook) {
                    console.log("❌ Selected time no longer available with new services, resetting...");
                    setSelectedTime("");
                }
            }
        }
        // eslint-disable-next-line
    }, [services]);

    const fetchAvailableTimes = async () => {
        console.log("🔄 fetchAvailableTimes called with date:", selectedDate);
        setIsLoadingTimes(true);
        setError("");
        
        // Reset states beim Laden
        setBookedSlots(new Set());
        setAvailableTimes({ free: [], all: [], booked: [] });
        
        try {
            // Direkter Supabase-Check für alle Termine an diesem Tag (00:00 - 24:00)
            const startOfDay = new Date(selectedDate);
            startOfDay.setHours(0, 0, 0, 0);
            const nextDay = new Date(startOfDay);
            nextDay.setDate(startOfDay.getDate() + 1);

            const { data: appointments, error } = await supabase
                .from("appointments")
                .select("time, service, name, date")
                .gte("date", startOfDay.toISOString())
                .lt("date", nextDay.toISOString());

            if (error) {
                console.error("Supabase error:", error);
                throw error;
            }

            console.log("Found appointments:", appointments);

            const allSlots = generateTimeSlots();
            const bookedSlots = new Set();
            const bookedDetails = new Map(); // Speichere Details für jeden belegten Slot

            // Verarbeite jeden Termin
            appointments.forEach(apt => {
                const startTime = normalizeTime(apt.time);
                console.log("Processing appointment:", apt);
                
                // Berechne Dauer basierend auf Service
                let duration = 30; // Standard
                if (apt.service) {
                    const services = apt.service.split(", ");
                    duration = getTotalDuration(services);
                }
                
                const startIndex = allSlots.indexOf(startTime);
                console.log(`Appointment: ${startTime}, Duration: ${duration}min, StartIndex: ${startIndex}`);
                
                if (startIndex !== -1) {
                    const slotsToBlock = Math.ceil(duration / 30);
                    for (let i = 0; i < slotsToBlock; i++) {
                        if (startIndex + i < allSlots.length) {
                            const slotToBlock = allSlots[startIndex + i];
                            bookedSlots.add(slotToBlock);
                            bookedDetails.set(slotToBlock, {
                                name: apt.name,
                                service: apt.service,
                                startTime: startTime,
                                duration: duration
                            });
                            console.log(`Blocking slot: ${slotToBlock} for ${apt.name}`);
                        }
                    }
                }
            });

            const freeSlots = allSlots.filter(slot => !bookedSlots.has(slot));
            console.log("Final result:", { 
                free: freeSlots, 
                all: allSlots, 
                booked: Array.from(bookedSlots),
                bookedDetails: Object.fromEntries(bookedDetails)
            });
            
            console.log("✅ Setting new available times:", {
                free: freeSlots.length,
                all: allSlots.length,
                booked: Array.from(bookedSlots).length,
                bookedDetails: Object.keys(Object.fromEntries(bookedDetails)).length
            });
            
            setAvailableTimes({ 
                free: freeSlots, 
                all: allSlots, 
                booked: Array.from(bookedSlots),
                bookedDetails: bookedDetails
            });
            
            // Zusätzlicher State für einfachere Checks
            setBookedSlots(bookedSlots);
            
            console.log("✅ States updated successfully");
        } catch (err) {
            console.error("Error fetching available times:", err);
            setError(`Fehler beim Laden der verfügbaren Zeiten: ${err.message || 'Unbekannter Fehler'}`);
        } finally {
            setIsLoadingTimes(false);
        }
    };

    const handleBook = async () => {
        if (!selectedUser || !selectedDate || !selectedTime || !services.length) {
            setError("Bitte füllen Sie alle Felder aus.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            console.log("🚨 FINAL CHECK - Attempting to book:", { selectedTime, selectedDate });
            
            // FINAL CHECK: Supabase direkt prüfen - gesamte Tagesüberschneidung vermeiden
            const startOfDay = new Date(selectedDate);
            startOfDay.setHours(0, 0, 0, 0);
            const nextDay = new Date(startOfDay);
            nextDay.setDate(startOfDay.getDate() + 1);

            const { data: dayAppointments, error: checkError } = await supabase
                .from("appointments")
                .select("time, service, name, id")
                .gte("date", startOfDay.toISOString())
                .lt("date", nextDay.toISOString());

            if (checkError) {
                console.error("Error checking availability:", checkError);
                throw new Error("Fehler beim Prüfen der Verfügbarkeit");
            }

            console.log("🔍 Existing appointments that day:", dayAppointments);

            // Prüfe Überschneidung anhand der geblockten 30-Minuten-Slots
            const allSlots = generateTimeSlots();
            const occupiedSlots = new Set();
            (dayAppointments || []).forEach(apt => {
                const startTime = normalizeTime(apt.time);
                let duration = 30;
                if (apt.service) {
                    const servicesArr = apt.service.split(", ");
                    duration = getTotalDuration(servicesArr);
                }
                const startIndex = allSlots.indexOf(startTime);
                if (startIndex !== -1) {
                    const slotsToBlock = Math.ceil(duration / 30);
                    for (let i = 0; i < slotsToBlock; i++) {
                        if (startIndex + i < allSlots.length) {
                            occupiedSlots.add(allSlots[startIndex + i]);
                        }
                    }
                }
            });

            const slotsNeeded = Math.ceil(getTotalDuration(services) / 30);
            const selectedIndex = allSlots.indexOf(selectedTime);
            if (selectedIndex === -1) {
                throw new Error("Ungültige Zeit ausgewählt.");
            }
            
            // Spezialfall: 19:30 ist immer buchbar (letzter Slot des Tages)
            if (selectedTime !== "19:30") {
                for (let i = 0; i < slotsNeeded; i++) {
                    const checkSlot = allSlots[selectedIndex + i];
                    if (!checkSlot || occupiedSlots.has(checkSlot)) {
                        throw new Error(`❌ DOPPELBUCHUNG VERHINDERT: Zeit ${selectedTime} überschneidet sich mit bestehendem Termin.`);
                    }
                }
            }
            
            console.log("✅ Time slot is free - proceeding with booking");

            const totalDuration = getTotalDuration(services);

            // Hole den Benutzernamen für den Termin
            const selectedUserData = users.find(u => u.id === selectedUser);
            const userName = selectedUserData ? selectedUserData.username : "Unbekannter Benutzer";
            
            console.log("📝 Creating appointment for:", userName, "at", selectedTime, "on", selectedDate);
            
            // ISO-Datum mit Uhrzeit speichern (Konsistenz mit anderer Buchungslogik)
            const selectedDateTime = new Date(selectedDate);
            const [hours, minutes] = selectedTime.split(":").map(Number);
            selectedDateTime.setHours(hours, minutes, 0, 0);

            const { error } = await supabase.from("appointments").insert({
                user_id: selectedUser,
                name: userName,
                date: selectedDateTime.toISOString(),
                time: selectedTime,
                service: services.join(", ")
            });

            if (error) {
                console.error("Supabase insert error:", error);
                throw error;
            }

            console.log("✅ Appointment created successfully!");
            setSuccess("Termin erfolgreich erstellt!");
            setSelectedUser("");
            setSelectedDate(formatLocalYYYYMMDD(new Date()));
            setSelectedTime("");
            setServices([]);
            fetchAvailableTimes();
        } catch (err) {
            console.error("❌ Error creating appointment:", err);
            setError(`Fehler beim Erstellen des Termins: ${err.message || 'Unbekannter Fehler'}`);
        } finally {
            setLoading(false);
        }
    };

    // Gefilterte User-Liste für die Suche
    const filteredUsers = users.filter(u =>
        u.username.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 mb-4 shadow-2xl max-w-full mx-auto overflow-y-auto max-h-[500px]">
            <h3 className="text-base font-semibold text-zinc-100 mb-3 text-center">Termin für Nutzer anlegen</h3>
            {error && (
                <div className="mb-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start space-x-2">
                    <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-300 text-sm">{error}</p>
                </div>
            )}
            {success && (
                <div className="mb-3 p-3 bg-green-900/20 border border-green-500/30 rounded-lg flex items-start space-x-2">
                    <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-green-300 text-sm">{success}</p>
                </div>
            )}
            
            {/* Nutzer-Auswahl */}
            <div className="mb-3">
                <label className="block text-zinc-300 mb-2 text-sm font-medium">Nutzer wählen</label>
                <button
                    type="button"
                    className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-zinc-100 focus:ring-2 focus:ring-zinc-500 focus:border-zinc-500 flex justify-between items-center cursor-pointer transition-all duration-200 hover:border-zinc-500"
                    onClick={() => setUserModalOpen(true)}
                >
                    <span className={selectedUser ? "text-zinc-100" : "text-zinc-500"}>{selectedUser ? (users.find(u => u.id === selectedUser)?.username || "") : 'Nutzer wählen'}</span>
                    <svg className="w-4 h-4 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </button>
                
                {/* User Modal */}
                {userModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4" onClick={() => setUserModalOpen(false)}>
                        <div className="bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-xs sm:max-w-sm max-h-[85vh] sm:max-h-[80vh] overflow-y-auto mx-2 sm:mx-0" onClick={e => e.stopPropagation()}>
                            <div className="p-3 sm:p-4">
                                <h3 className="text-sm sm:text-base font-bold text-zinc-100 mb-2 sm:mb-3 text-center">Nutzer wählen</h3>
                                <input
                                    type="text"
                                    placeholder="Nutzer suchen..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-full mb-2 sm:mb-3 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-zinc-100 placeholder-zinc-500 focus:ring-2 focus:ring-zinc-500 focus:border-zinc-500 outline-none text-sm sm:text-base"
                                />
                                <div className="max-h-40 sm:max-h-48 overflow-auto flex flex-col gap-1 sm:gap-2">
                                    {filteredUsers.length === 0 && (
                                        <div className="text-zinc-400 text-center py-3 sm:py-4 text-xs sm:text-sm">Kein Nutzer gefunden</div>
                                    )}
                                    {filteredUsers.map(user => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => { setSelectedUser(user.id); setUserModalOpen(false); setSearch(""); }}
                                            className={`w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-all duration-150 text-xs sm:text-sm font-medium focus:outline-none flex items-center gap-2
                                                ${selectedUser === user.id ? 'bg-zinc-600 border border-zinc-500 text-zinc-100 ring-2 ring-zinc-400' :
                                                    'bg-zinc-900 border border-zinc-600 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800'}
                                            `}
                                        >
                                            {user.username}
                                        </button>
                                    ))}
                                </div>
                                <button className="absolute top-2 sm:top-3 right-2 sm:right-3 text-zinc-400 hover:text-zinc-200 p-1 transition-colors duration-150" onClick={() => setUserModalOpen(false)} aria-label="Schließen">
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Datum-Auswahl */}
            {selectedUser && (
                <div className="mb-3">
                    <label className="block text-zinc-300 mb-2 text-sm font-medium">Datum wählen</label>
                    <div className="flex space-x-2">
                        <input
                            type="text"
                            value={parseLocalYYYYMMDD(selectedDate).toLocaleDateString("de-DE", { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                            })}
                            readOnly
                            className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-100 cursor-pointer"
                        />
                        <button
                            onClick={() => setCalendarModalOpen(true)}
                            className="bg-zinc-700 hover:bg-zinc-600 text-zinc-100 px-4 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center"
                            title="Kalender öffnen"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Dienstleistungs-Auswahl */}
            <div className="w-full mb-3">
                <label className="block text-zinc-300 mb-2 text-sm font-medium">Dienstleistungen</label>
                <button
                    type="button"
                    className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-zinc-100 focus:ring-2 focus:ring-zinc-500 focus:border-zinc-500 flex justify-between items-center cursor-pointer transition-all duration-200 hover:border-zinc-500"
                    onClick={() => setServiceModalOpen(true)}
                >
                    <span className={services.length ? "text-zinc-100" : "text-zinc-500"}>{services.length ? services.join(", ") : 'Dienstleistungen wählen'}</span>
                    <svg className="w-4 h-4 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </button>
                
                {/* Service Modal */}
                {serviceModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4" onClick={() => setServiceModalOpen(false)}>
                        <div className="bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-xs sm:max-w-sm max-h-[85vh] sm:max-h-[80vh] overflow-y-auto mx-2 sm:mx-0" onClick={e => e.stopPropagation()}>
                            <div className="p-3 sm:p-4">
                                <h3 className="text-sm sm:text-base font-bold text-zinc-100 mb-3 sm:mb-4 text-center">Dienstleistungen wählen</h3>
                                <div className="flex flex-col gap-2 sm:gap-3">
                                    {[
                                        { label: "Haare", value: "Haare" },
                                        { label: "Bart", value: "Bart" },
                                        { label: "Haare und Bart", value: "Haare und Bart" },
                                        { label: "Augenbrauen zupfen", value: "Augenbrauen zupfen" },
                                    ].map((opt) => {
                                        let disabled = false;
                                        if (opt.value === "Haare und Bart" && (services.includes("Haare") || services.includes("Bart"))) {
                                            disabled = true;
                                        }
                                        if ((opt.value === "Haare" || opt.value === "Bart") && services.includes("Haare und Bart")) {
                                            disabled = true;
                                        }
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                disabled={disabled}
                                                onClick={() => {
                                                    if (disabled) return;
                                                    if (services.includes(opt.value)) {
                                                        setServices(services.filter(s => s !== opt.value));
                                                    } else {
                                                        setServices([...services, opt.value]);
                                                    }
                                                }}
                                                className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-150 text-xs sm:text-sm font-medium focus:outline-none flex items-center justify-between
                                                    ${services.includes(opt.value)
                                                        ? 'bg-zinc-600 border border-zinc-500 text-zinc-100 ring-2 ring-zinc-400'
                                                        : disabled
                                                            ? 'bg-zinc-900 border border-zinc-700 text-zinc-500 opacity-60 cursor-not-allowed'
                                                            : 'bg-zinc-900 border border-zinc-600 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800'}
                                                `}
                                            >
                                                <span>{opt.label}</span>
                                                {services.includes(opt.value) && !disabled && (
                                                    <span className="text-zinc-300">
                                                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button className="absolute top-2 sm:top-3 right-2 sm:right-3 text-zinc-400 hover:text-zinc-200 p-1 transition-colors duration-150" onClick={() => setServiceModalOpen(false)} aria-label="Schließen">
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {error && !services.length && <span className="text-red-400 text-xs mt-1">Bitte wählen Sie mindestens eine Dienstleistung aus.</span>}
            </div>

            {/* Zeit-Auswahl */}
            {selectedUser && selectedDate && services.length > 0 && !isLoadingTimes && availableTimes.all.length > 0 && (
                <div className="mb-3">
                    <label className="block text-zinc-300 mb-2 text-sm font-medium">Uhrzeit</label>
                    
                    {/* Warnung bei Dienstleistungsdauer */}
                    {(() => {
                        const totalDuration = getTotalDuration(services);
                        const blocksNeeded = Math.ceil(totalDuration / 30);
                        const availableSlots = availableTimes.all.filter(slot => {
                            const isBooked = bookedSlots.has(slot);
                            if (isBooked) return false;
                            
                            // Spezialfall: 19:30 ist immer verfügbar
                            if (slot === "19:30") return true;
                            
                            const slotIndex = availableTimes.all.indexOf(slot);
                            for (let i = 0; i < blocksNeeded; i++) {
                                const nextSlot = availableTimes.all[slotIndex + i];
                                if (!nextSlot || bookedSlots.has(nextSlot) === true) {
                                    return false;
                                }
                            }
                            return true;
                        });
                        
                        if (availableSlots.length === 0) {
                            return (
                                <div className="mb-3 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                                    <div className="flex items-start space-x-2">
                                        <svg className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <div>
                                            <p className="text-orange-300 text-sm font-medium">⚠️ Keine verfügbaren Zeiten</p>
                                            <p className="text-orange-200 text-xs mt-1">
                                                Die gewählten Dienstleistungen benötigen <strong>{totalDuration} Minuten</strong> ({blocksNeeded} Zeitblöcke). 
                                                An diesem Tag sind keine zusammenhängenden Zeitblöcke verfügbar.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        } else if (availableSlots.length < 3) {
                            return (
                                <div className="mb-3 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                                    <div className="flex items-start space-x-2">
                                        <svg className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <div>
                                            <p className="text-yellow-300 text-sm font-medium">⚠️ Wenige verfügbare Zeiten</p>
                                            <p className="text-yellow-200 text-xs mt-1">
                                                Nur <strong>{availableSlots.length}</strong> Zeit{availableSlots.length === 1 ? 'slot' : 'slots'} verfügbar für {totalDuration} Minuten ({blocksNeeded} Blöcke).
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}
                    {isLoadingTimes ? (
                        <div className="text-center text-zinc-400 py-4">Lade verfügbare Zeiten...</div>
                    ) : (
                        <div className="flex flex-col">
                            <div className="flex flex-wrap gap-1 sm:gap-2 justify-center max-h-32 overflow-y-auto mb-2 px-1">
                                {availableTimes.all.map((slot) => {
                                    const isBooked = bookedSlots.has(slot);
                                    const bookedInfo = availableTimes.bookedDetails?.get(slot);

                                    // Prüfe, ob ab diesem Slot genug zusammenhängende freie Blöcke vorhanden sind
                                    const totalDuration = getTotalDuration(services);
                                    const blocksNeeded = Math.ceil(totalDuration / 30);
                                    const slotIndex = availableTimes.all.indexOf(slot);
                                    let hasContiguousCapacity = true;
                                    
                                    // Spezialfall: 19:30 ist immer verfügbar (letzter Slot des Tages)
                                    if (slot === "19:30") {
                                        hasContiguousCapacity = true;
                                    } else if (blocksNeeded > 0) {
                                        for (let i = 0; i < blocksNeeded; i++) {
                                            const nextSlot = availableTimes.all[slotIndex + i];
                                            if (!nextSlot || bookedSlots.has(nextSlot) === true) {
                                                hasContiguousCapacity = false;
                                                break;
                                            }
                                        }
                                    }

                                    const isSelectable = !isBooked && hasContiguousCapacity;

                                    return (
                                        <button
                                            key={slot}
                                            type="button"
                                            onClick={() => {
                                                if (isSelectable) {
                                                    setSelectedTime(slot);
                                                }
                                            }}
                                            disabled={!isSelectable}
                                            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 flex-shrink-0 ${
                                                selectedTime === slot
                                                    ? 'bg-zinc-600 text-zinc-100 ring-2 ring-zinc-400'
                                                    : !isSelectable
                                                    ? (isBooked
                                                        ? 'bg-red-900/70 text-red-100 border-2 border-red-400 cursor-not-allowed opacity-100'
                                                        : 'bg-zinc-900/40 text-zinc-500 border border-zinc-700 cursor-not-allowed')
                                                    : 'bg-zinc-900 text-zinc-200 border border-zinc-600 hover:border-zinc-500 hover:bg-zinc-800'
                                            }`}
                                            title={
                                                isBooked && bookedInfo
                                                    ? `🚫 BELEGT: ${bookedInfo.name} - ${bookedInfo.service} (${bookedInfo.duration}min)`
                                                    : !hasContiguousCapacity
                                                        ? '🚫 Nicht genug Zeit am Stück verfügbar'
                                                        : '✅ Verfügbar'
                                            }
                                        >
                                            <div className="flex flex-col items-center">
                                                <span>{slot}</span>
                                                {isBooked && (
                                                    <div className="flex items-center gap-0.5 sm:gap-1 mt-0.5 sm:mt-1">
                                                        <span className="text-xs">🔒</span>
                                                        {bookedInfo && (
                                                            <span className="text-xs text-red-200 truncate max-w-12 sm:max-w-16">
                                                                {bookedInfo.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Legende */}
                            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-xs text-zinc-400 mt-2">
                                <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-zinc-900 border border-zinc-600 rounded"></div>
                                    <span>Verfügbar</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-red-900/30 border border-red-600 rounded"></div>
                                    <span>Gebucht</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-zinc-600 rounded"></div>
                                    <span>Ausgewählt</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-zinc-900/40 border border-zinc-700 rounded"></div>
                                    <span>Nicht genug Zeit</span>
                                </div>
                            </div>
                            
                            {selectedTime && (
                                <div className="text-center text-zinc-300 text-sm mt-2">
                                    Gewählte Zeit: {selectedTime} ({getTotalDuration(services)} Min.)
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Buchen Button */}
            {selectedUser && selectedDate && selectedTime && services.length > 0 && (
                <div className="mt-4">
                    <button
                        className="w-full bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-zinc-100 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                        onClick={handleBook}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Wird erstellt...</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>Termin buchen</span>
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Calendar Modal */}
            {calendarModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4" onClick={() => setCalendarModalOpen(false)}>
                    <div className="bg-zinc-800 border border-zinc-700 rounded-xl shadow-xl w-full max-w-sm sm:max-w-md mx-2 sm:mx-4 p-0 relative flex flex-col max-h-[80vh] sm:max-h-[75vh]" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-700">
                            <h3 className="text-base sm:text-lg font-semibold text-zinc-100">Datum auswählen</h3>
                            <button 
                                onClick={() => setCalendarModalOpen(false)}
                                className="text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Calendar Content */}
                        <div className="p-2 sm:p-3 flex-1 flex flex-col -ml-2">
                            <div className="flex-1 flex flex-col space-y-1 sm:space-y-2">
                                {/* Month Navigation */}
                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={() => {
                                            const newDate = parseLocalYYYYMMDD(selectedDate);
                                            newDate.setMonth(newDate.getMonth() - 1);
                                            setSelectedDate(formatLocalYYYYMMDD(newDate));
                                        }}
                                        className="p-2 hover:bg-zinc-700 rounded-lg transition-colors duration-200"
                                    >
                                        <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h4 className="text-lg font-semibold text-zinc-100">
                                        {parseLocalYYYYMMDD(selectedDate).toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                                    </h4>
                                    <button
                                        onClick={() => {
                                            const newDate = parseLocalYYYYMMDD(selectedDate);
                                            newDate.setMonth(newDate.getMonth() + 1);
                                            setSelectedDate(formatLocalYYYYMMDD(newDate));
                                        }}
                                        className="p-2 hover:bg-zinc-700 rounded-lg transition-colors duration-200"
                                    >
                                        <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Weekday Headers */}
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                    {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'].map(day => (
                                        <div key={day} className="text-center text-sm font-medium text-zinc-400 py-2">
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                {/* Calendar Grid */}
                                <div className="grid grid-cols-7 gap-1 flex-1 overflow-hidden">
                                    {(() => {
                                        const date = parseLocalYYYYMMDD(selectedDate);
                                        const year = date.getFullYear();
                                        const month = date.getMonth();
                                        const firstDay = new Date(year, month, 1);
                                        const lastDay = new Date(year, month + 1, 0);
                                        const startDate = new Date(firstDay);
                                        startDate.setDate(startDate.getDate() - firstDay.getDay());
                                        
                                        const days = [];
                                        const today = new Date();
                                        const maxDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                                        
                                        for (let i = 0; i < 42; i++) {
                                            const dayDate = new Date(startDate);
                                            dayDate.setDate(startDate.getDate() + i);
                                            
                                            const isCurrentMonth = dayDate.getMonth() === month;
                                            const isToday = dayDate.toDateString() === today.toDateString();
                                            const isSelected = formatLocalYYYYMMDD(dayDate) === selectedDate;
                                            const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
                                            const isPast = dayDate < today && !isToday;
                                            const isTooFar = dayDate > maxDate;
                                            
                                            days.push(
                                                <button
                                                    key={i}
                                                    onClick={() => {
                                                        if (!isPast && !isWeekend && !isTooFar) {
                                                            setSelectedDate(formatLocalYYYYMMDD(dayDate));
                                                            setCalendarModalOpen(false);
                                                            setSelectedTime("");
                                                        }
                                                    }}
                                                    disabled={isPast || isWeekend || isTooFar}
                                                    className={`h-full w-full rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center min-h-[40px] ${!isCurrentMonth ? 'text-zinc-600' : ''} ${isPast || isWeekend || isTooFar ? 'text-zinc-500 cursor-not-allowed' : 'hover:bg-zinc-700 cursor-pointer'} ${isToday ? 'bg-blue-600 text-white font-bold' : ''} ${isSelected ? 'bg-zinc-500 text-white font-bold' : ''} ${isCurrentMonth && !isPast && !isToday && !isSelected && !isWeekend && !isTooFar ? 'text-zinc-300' : ''}`}
                                                >
                                                    {dayDate.getDate()}
                                                </button>
                                            );
                                        }
                                        return days;
                                    })()}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center justify-center space-x-4 text-sm text-zinc-400 pt-3 border-t border-zinc-700">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 bg-blue-600 rounded"></div>
                                        <span>Heute</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 bg-zinc-500 rounded"></div>
                                        <span>Ausgewählt</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex justify-end gap-2 p-2 border-t border-zinc-700 bg-zinc-900 rounded-b-xl">
                            <button 
                                onClick={() => setCalendarModalOpen(false)}
                                className="px-3 py-1.5 bg-zinc-600 hover:bg-zinc-500 text-zinc-100 rounded transition-colors duration-200 text-sm"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminCreateAppointment;